import React from "react"
import { useSelector, useDispatch } from "react-redux"

import {
    IDLE, EDIT, DELETE, YES_NO, NUMBER, TEXT, PENDING,
} from "const"
import { RrsCategory } from "server/models"
import { hasAccess, hasProjectAccess, deepCopy } from "client/utils"
import {
    PRM_RRS_CATEGORY,
    StoreType,
    RrsCategoryList,
    ChangeRrsCategoryStatus,

    setRrsCategoryList,
    setRrsCategoryStatusActive,
    setRrsCategoryActive,
    setRrsCategoryShowConfirmDelete,
    addModal,
} from "client/store"
import {
    Link,
    StatusSwitcher,
    DeleteButton,
    TableColumn,
    TablePopoverData,
    TablePopover,
} from "client/components"

const Columns = () => {
    const baseRoute = useSelector((state: StoreType) => state.rrsCategory.baseRoute)
    const categoryList = useSelector((state: StoreType) => state.rrsCategory.categoryList)
    const childParent = useSelector((state: StoreType) => state.rrsCategory.categoryChildParent)
    const filters = useSelector((state: StoreType) => state.rrsCategory.filters)
    const statusActive = useSelector((state: StoreType) => state.rrsCategory.statusActive)

    const project = useSelector((state: StoreType) => state.project.projectView)
    const userData = useSelector((state: StoreType) => state.system.userData)
    const lang = useSelector((state: StoreType) => state.system.lang)
    const dispatch = useDispatch()

    const onStatusChange = (category: RrsCategory) => {
        if (statusActive !== null) return
        dispatch(setRrsCategoryStatusActive(category.id))
        ChangeRrsCategoryStatus(category).then((status) => {
            const categoryListCopy: RrsCategoryList = deepCopy(categoryList)
            categoryListCopy[category.id] = {
                ...categoryListCopy[category.id],
                status,
            }
            dispatch(setRrsCategoryList(categoryListCopy))
            dispatch(setRrsCategoryStatusActive(null))
        })
    }

    const editAccess = !project.id
        ? hasAccess(PRM_RRS_CATEGORY, EDIT)
        : hasProjectAccess(PRM_RRS_CATEGORY, EDIT)

    const deleteAccess = !project.id
        ? hasAccess(PRM_RRS_CATEGORY, DELETE)
        : hasProjectAccess(PRM_RRS_CATEGORY, DELETE)

    const columns: TableColumn[] = [
        {
            field: "name",
            header: lang.name,
            collapsible: true,
            cellMinWidth: "200px",
            formatter: (category: RrsCategory) => {
                let categoryName: string

                if (filters && filters.search && filters.search.length) {
                    const parentArray: string[] = []
                    const addParent = (parentId: number) => {
                        if (parentId) {
                            const parent = categoryList[parentId]
                            if (parent) parentArray.unshift(parent.shortName.length ? parent.shortName : parent.name)
                            addParent(childParent[parentId])
                        }
                    }
                    addParent(category.id)
                    categoryName = parentArray.length ? parentArray.join(" > ") : category.name
                } else {
                    categoryName = category.name
                }

                if (editAccess) {
                    const href = `${baseRoute}/${EDIT}/${category.id}`
                    return (
                        <Link
                            href={href}
                            onClick={() => dispatch(addModal({ url: href }))}
                        >
                            {categoryName}
                        </Link>
                    )
                }
                return categoryName
            },
        },
        {
            field: "shortName",
            header: lang.shortName,
            cellMinWidth: "100px",
        },
        {
            field: "description",
            header: lang.description,
            cellMinWidth: "200px",
            cellMaxWidth: "300px",
        },
        {
            field: "order",
            header: lang.order,
            cellMinWidth: "100px",
        },
        {
            field: "customVariables",
            header: lang.customVariables,
            cellMinWidth: "150px",
            formatter: (category: RrsCategory) => {
                const dataList: TablePopoverData[] = []
                if (category.customVariablesParsed) {
                    category.customVariablesParsed.forEach((custom) => {
                        let value: string
                        if (custom.type === YES_NO) {
                            if (custom.value === true) value = lang.yes
                            if (custom.value === false) value = lang.no
                        }
                        if (custom.type === NUMBER || custom.type === TEXT) {
                            value = custom.value.toString()
                        }

                        dataList.push({
                            id: custom.index.toString(),
                            title: `${custom.name}: ${value}`,
                        })
                    })
                }

                return (
                    <TablePopover
                        id={`customVariables${category.id}`}
                        dataList={dataList}
                        placement="left"
                        listStyle
                        title={lang.customVariables}
                        showFirst={dataList.length === 1}
                        editAccess={false}
                    />
                )
            },
        },
    ]

    if (userData.accountId === 1) {
        columns.push(
            {
                field: "status",
                header: lang.status,
                centerAlign: true,
                formatter: (category: RrsCategory) => category.categoryId === null && (
                    <StatusSwitcher
                        status={statusActive === category.id ? PENDING : category.status}
                        onClick={() => onStatusChange(category)}
                    />
                ),
            },
        )
    }

    if (deleteAccess) {
        columns.push({
            field: "deleteCategory",
            centerAlign: true,
            cellWidth: "46px",
            formatter: (category: RrsCategory) => (
                <DeleteButton
                    status={IDLE}
                    onClick={() => {
                        dispatch(setRrsCategoryActive(category))
                        dispatch(setRrsCategoryShowConfirmDelete(true))
                    }}
                />
            ),
        })
    }

    return columns
}
export default Columns
